import { useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch, apiImageUrl } from '../utils/streamApi';

// Embeddable live view for a single patrol checkpoint — live frame + zone +
// detected skeleton on the left, a big-font §31-style readout on the right,
// mirroring the Camera Test Mode screen. Used on the Report page so a supervisor
// can watch a check-in. Runs pose via /ui/patrol/test only while mounted (opt-in).
const COCO_EDGES = [[5, 7], [7, 9], [6, 8], [8, 10], [5, 6], [5, 11], [6, 12], [11, 12], [11, 13], [13, 15], [12, 14], [14, 16]];

export default function PatrolLiveView({ cameraIdx, polygon = [], expectedGuard = null, holdSeconds = 2 }) {
  const [imgUrl, setImgUrl]   = useState('');
  const [readout, setReadout] = useState(null);
  const [heldMs, setHeldMs]   = useState(0);
  const imgRef = useRef(null), canvasRef = useRef(null);
  const holdStart = useRef(null), lastRaised = useRef(0), busy = useRef(false);
  const hasCam = cameraIdx !== undefined && cameraIdx !== null && cameraIdx !== '';
  const polyKey = JSON.stringify(polygon);

  const refreshFrame = useCallback(async () => {
    if (!hasCam) return;
    try { setImgUrl(await apiImageUrl(`/cameras/${cameraIdx}/frame?ts=${Date.now()}`)); } catch { /* keep last */ }
  }, [cameraIdx, hasCam]);

  useEffect(() => { if (!hasCam) return; refreshFrame(); const t = setInterval(refreshFrame, 800); return () => clearInterval(t); }, [refreshFrame, hasCam]);
  useEffect(() => { setImgUrl(''); setReadout(null); holdStart.current = null; lastRaised.current = 0; busy.current = false; setHeldMs(0); }, [cameraIdx]);

  useEffect(() => {
    if (!hasCam) return;
    let alive = true;
    const poll = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const r = await apiFetch('/ui/patrol/test', { method: 'POST', json: { camera_idx: Number(cameraIdx), polygon, expected_guard: expectedGuard || null } });
        if (!alive) return;
        const d = r.ok ? await r.json() : null;
        setReadout(d);
        const now = Date.now();
        const raised = !!(d && (d.left_raised || d.right_raised));
        if (raised) { lastRaised.current = now; if (!holdStart.current) holdStart.current = now; setHeldMs(now - holdStart.current); }
        else if (holdStart.current && now - lastRaised.current > 800) { holdStart.current = null; setHeldMs(0); }
      } catch { /* transient */ }
      finally { busy.current = false; }
    };
    poll();
    const t = setInterval(poll, 500);
    return () => { alive = false; clearInterval(t); };
  }, [cameraIdx, polyKey, expectedGuard]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cv = canvasRef.current, img = imgRef.current;
    if (!cv || !img) return;
    cv.width = img.clientWidth; cv.height = img.clientHeight;
    const ctx = cv.getContext('2d'); const W = cv.width, H = cv.height; ctx.clearRect(0, 0, W, H);
    if (polygon.length >= 3) {
      ctx.beginPath(); polygon.forEach(([x, y], i) => { const px = x * W, py = y * H; i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); }); ctx.closePath();
      ctx.strokeStyle = readout?.in_zone ? '#5cc98a' : '#4da3ff';
      ctx.fillStyle = readout?.in_zone ? 'rgba(92,201,138,0.15)' : 'rgba(77,163,255,0.12)';
      ctx.lineWidth = 2; ctx.fill(); ctx.stroke();
    }
    (readout?.persons || []).forEach(p => {
      const kp = p.kpts; ctx.strokeStyle = '#ffd24d'; ctx.lineWidth = 2;
      COCO_EDGES.forEach(([a, b]) => { if (kp[a][2] >= 0.3 && kp[b][2] >= 0.3) { ctx.beginPath(); ctx.moveTo(kp[a][0] * W, kp[a][1] * H); ctx.lineTo(kp[b][0] * W, kp[b][1] * H); ctx.stroke(); } });
      ctx.fillStyle = '#ffd24d'; kp.forEach(k => { if (k[2] >= 0.3) { ctx.beginPath(); ctx.arc(k[0] * W, k[1] * H, 3, 0, 6.2832); ctx.fill(); } });
    });
  }, [readout, polygon]);

  const held = heldMs / 1000;
  const done = !!(readout?.in_zone && readout?.pose_available && held >= Number(holdSeconds));

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ position: 'relative', flex: '1 1 380px', maxWidth: 640, background: '#000', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        {imgUrl
          ? <img key={cameraIdx} ref={imgRef} src={imgUrl} alt="live" onLoad={() => { /* redraw via readout */ }} style={{ display: 'block', width: '100%', height: 'auto' }} />
          : <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading camera…</div>}
        <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
      </div>

      {/* right-side readout — big fonts, like Test Mode */}
      <div style={{ flex: '0 0 260px', minWidth: 240, padding: '4px 4px' }}>
        <Row label="Person detected"    ok={readout?.person_detected} value={readout?.person_detected ? 'YES' : 'NO'} />
        <Row label="Inside patrol zone" ok={readout?.in_zone}         value={readout?.in_zone ? 'YES' : 'NO'} />
        <Row label="Pose available"     ok={readout?.pose_available}  value={readout?.pose_available ? 'YES' : 'NO'} />
        <Row label="Left hand"  ok={readout?.left_raised || null}  value={readout?.left_raised ? 'RAISED' : '—'} />
        <Row label="Right hand" ok={readout?.right_raised || null} value={readout?.right_raised ? 'RAISED' : '—'} />
        <Row label="Gesture held" ok={held >= Number(holdSeconds) || null} value={`${held.toFixed(1)} / ${Number(holdSeconds).toFixed(1)} s`} />
        <Row label="Face" ok={readout?.face_status === 'VERIFIED' ? true : undefined} value={readout?.face_name || readout?.face_status || '—'} />
        <div style={{
          marginTop: 14, padding: '12px 16px', borderRadius: 10, textAlign: 'center', fontSize: 20, fontWeight: 800, letterSpacing: 0.5,
          background: done ? 'rgba(92,201,138,0.92)' : 'rgba(40,40,40,0.6)', color: done ? '#08130c' : 'var(--text-muted)',
          border: `1px solid ${done ? '#5cc98a' : 'var(--border)'}`,
        }}>{done ? '✓ DONE' : 'WAITING…'}</div>
      </div>
    </div>
  );
}

function Row({ label, value, ok }) {
  const color = ok === true ? '#5cc98a' : ok === false ? '#e07070' : 'var(--text)';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'baseline', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: 18, color }}>{value ?? '—'}</span>
    </div>
  );
}
